import { CommonModule, KeyValue } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';

type OrderType = 'original' | 'asc' | 'desc';
@Component({
  selector: 'jmkw-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectComponent implements OnInit {
  private _initialValue!: KeyValue<string, any>;
  formBuilder = inject(FormBuilder);
  destrRef = inject(DestroyRef);
  form = this.formBuilder.nonNullable.group({
    select: [new FormControl<KeyValue<string, any>>(this._initialValue)]
  });
  sortOrderFn: (a: KeyValue<string, any>, b: KeyValue<string, any>) => number =
    originalOrder;
  private _orderFunctionMap: Record<
    OrderType,
    (a: KeyValue<string, any>, b: KeyValue<string, any>) => number
  > = {
    original: originalOrder,
    asc: keyAscOrder,
    desc: keyDescOrder
  };

  @Input() set order(
    order:
      | OrderType
      | ((a: KeyValue<string, any>, b: KeyValue<string, any>) => number)
  ) {
    if (typeof order === 'function') {
      this.sortOrderFn = order;
    } else if (typeof order === 'string' || typeof order === 'number') {
      this.sortOrderFn = this._orderFunctionMap[order];
    } else {
      throw new Error('Invalid order type or function');
    }
  }
  @Input() iconName?: string;
  @Input() labelPosition?: 'top' | 'left';
  @Input() fontSizeRem = 1;

  @Input() label!: string;
  @Input() set initialKeyValue(keyValue: KeyValue<string, any>) {
    this._initialValue = this.items[keyValue.key];
    this.form
      .get('select')
      ?.patchValue(this._initialValue, { emitEvent: false });
  }
  @Input() set initialKey(initialKey: keyof typeof this.items | undefined) {
    if (initialKey) {
      this._initialValue = { key: initialKey, value: this.items[initialKey] };
      this.form
        .get('select')
        ?.patchValue(this._initialValue, { emitEvent: false });
    }
  }
  @Input() set initialValue(initialValue: any) {
    const [key, value] = Object.entries(this.items).find(
      ([_, value]) => value === initialValue
    ) as [string, any];
    this._initialValue = { key, value };
    this.form
      .get('select')
      ?.patchValue(this._initialValue, { emitEvent: false });
  }
  @Input() items!: Record<string, any>;
  @Input() compareFn = (a: KeyValue<string, any>, b: KeyValue<string, any>) =>
    a.value === b.value;
  @Output() selectionChanged = new EventEmitter<KeyValue<string, any> | null>();

  ngOnInit(): void {
    this.form
      .get('select')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destrRef))
      .subscribe((value) => this.onSelectionChangeHandler(value));
  }

  onSelectionChangeHandler(value: KeyValue<string, any> | null) {
    this.selectionChanged.emit(value);
  }
}

// Order by ascending property value
export const originalOrder = (): number => {
  return 0;
};

// Order by ascending property value
export const keyAscOrder = (
  a: KeyValue<string, any>,
  b: KeyValue<string, any>
): number => {
  return a.key < b.key ? -1 : b.key < a.key ? 1 : 0;
};

// Order by descending property key
export const keyDescOrder = (
  a: KeyValue<string, any>,
  b: KeyValue<string, any>
): number => {
  return a.key > b.key ? -1 : b.key > a.key ? 1 : 0;
};
